import { getCurrencyFormatter } from "@redotech/money/util";
import { useHandler } from "@redotech/react-util/hook";
import ShieldTickIcon from "@redotech/redo-icons/shield-tick_thick.svg";
import {
  EWWidgetTextProps,
  ExtendedWarrantyDataTarget,
  getCheapestOffering,
  NO_COVERAGE_TIME_ID,
  Offering,
} from "@redotech/redo-model/extended-warranty";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import htmlReactParser from "html-react-parser";
import { memo, useState } from "react";

export const ExtendedWarrantyPopupToggleExperience = memo(
  function PopupToggleExperience({
    offerings,
    text,
    customCss,
    foundSelector,
    selectedOffering,
    variantId,
    updateSelectedOffering,
  }: {
    offerings: Offering[];
    text: EWWidgetTextProps;
    customCss?: string;
    foundSelector?: string;
    selectedOffering: Offering | undefined;
    variantId: string;
    updateSelectedOffering: (variantId: string, offering: Offering) => void;
  }) {
    const [hasBeenDeclined, setHasBeenDeclined] = useState(false);
    const cheapestOffering = getCheapestOffering(offerings);
    const noCoverageOffering = offerings.find(
      (o) => o.id === NO_COVERAGE_TIME_ID,
    );
    const ewIsAdded =
      selectedOffering && selectedOffering.id !== NO_COVERAGE_TIME_ID;

    const handleDecline = useHandler(() => {
      if (noCoverageOffering) {
        updateSelectedOffering(variantId, noCoverageOffering);
      }
      setHasBeenDeclined(true);
    });

    const handleAccept = useHandler(() => {
      if (cheapestOffering) {
        updateSelectedOffering(variantId, cheapestOffering);
      }
    });

    if (!cheapestOffering) {
      return null;
    }

    return (
      <>
        {customCss && <style>{customCss}</style>}
        {foundSelector && <meta found-selector={foundSelector} />}
        {ewIsAdded ? (
          <ProtectionAdded selectedOffering={selectedOffering} text={text} />
        ) : hasBeenDeclined ? null : (
          <PopupToggle
            cheapestOffering={cheapestOffering}
            handleAccept={handleAccept}
            handleDecline={handleDecline}
            text={text}
          />
        )}
      </>
    );
  },
);

const ProtectionAdded = memo(function ProtectionAdded({
  selectedOffering,
  text,
}: {
  selectedOffering: Offering;
  text: EWWidgetTextProps;
}) {
  return (
    <Flex
      align="center"
      data-target={ExtendedWarrantyDataTarget.POPUP_TOGGLE_CONTAINER}
      flexDirection="column"
      gap="sm"
    >
      <Flex
        align="center"
        data-target={ExtendedWarrantyDataTarget.POPUP_TITLE_CONTAINER}
        gap="sm"
      >
        <ShieldTickIcon
          data-target={ExtendedWarrantyDataTarget.POPUP_TOGGLE_SHIELD}
        />
        <Flex data-target={ExtendedWarrantyDataTarget.POPUP_ADDED_TITLE}>
          <Text fontSize="lg" fontWeight="medium">
            Purchase protected!
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
});

const PopupToggle = memo(function PopupToggle({
  text,
  cheapestOffering,
  handleAccept,
  handleDecline,
}: {
  text: EWWidgetTextProps;
  cheapestOffering: Offering;
  handleAccept: () => void;
  handleDecline: () => void;
}) {
  const formattedPrice = getCurrencyFormatter(
    cheapestOffering.price.currency,
  ).format(cheapestOffering.price.amount);
  const subText = text.popupTitleSubtext
    .replace("%plan_title%", cheapestOffering.title)
    .replace("%price%", formattedPrice);
  return (
    <Flex
      align="center"
      data-target={ExtendedWarrantyDataTarget.POPUP_TOGGLE_CONTAINER}
      flexDirection="column"
      gap="sm"
    >
      <Flex
        align="center"
        data-target={ExtendedWarrantyDataTarget.POPUP_TITLE_CONTAINER}
        gap="sm"
      >
        <ShieldTickIcon
          data-target={ExtendedWarrantyDataTarget.POPUP_TOGGLE_SHIELD}
        />
        <Flex
          data-target={ExtendedWarrantyDataTarget.POPUP_TITLE_CONTENT}
          flexDirection="column"
          gap="none"
        >
          <Flex data-target={ExtendedWarrantyDataTarget.POPUP_TITLE_TEXT}>
            <Text fontSize="lg" fontWeight="medium">
              {htmlReactParser(text.popupTitle)}
            </Text>
          </Flex>
          <Flex data-target={ExtendedWarrantyDataTarget.POPUP_TITLE_SUBTEXT}>
            <Text fontSize="md" fontWeight="regular">
              {htmlReactParser(subText)}
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <Flex data-target={ExtendedWarrantyDataTarget.POPUP_DESCRIPTION}>
        <Text fontSize="sm" fontWeight="regular" textAlign="center">
          {htmlReactParser(text.popupDescription)}
        </Text>
      </Flex>
      <Flex
        align="center"
        data-target={ExtendedWarrantyDataTarget.POPUP_BUTTONS_CONTAINER}
        flexDirection="column"
        gap="none"
      >
        <Flex data-target={ExtendedWarrantyDataTarget.POPUP_BUTTON_ACCEPT}>
          <RedoButton
            hierarchy="primary"
            onClick={handleAccept}
            size="sm"
            text={text.popupButtonAccept}
          />
        </Flex>
        <Flex data-target={ExtendedWarrantyDataTarget.POPUP_BUTTON_DECLINE}>
          <RedoButton
            hierarchy="link_underline"
            onClick={handleDecline}
            size="sm"
            text={text.popupButtonDecline}
          />
        </Flex>
      </Flex>
    </Flex>
  );
});
